import htmlDecode from "../utils/htmlDecode";
import { initClickHandler } from "../utils/initClickHandler";
import loadContextData from "../utils/loadContextData";
import createCounter from "../utils/createCounter";
import { URL_CHANGE_EVENT } from "../consts";

const setSlidesBarPosition = (thumbBar, slide) => {
    const slidesPerPage = 5;
    const midPoint = Math.ceil(slidesPerPage / 2);

    thumbBar.scrollLeft =
        Math.max(parseInt(slide.dataset.activeItem) - midPoint, 0) * slide.clientWidth;
};

const setHtml = (container, html) => {
    const contentWrapper = document.createElement("template");
    contentWrapper.innerHTML = html;
    container.firstElementChild.replaceWith(contentWrapper.content);
};

const setSlideHtml = (container, slide) => {
    const selector = `#${slide.dataset.mediaHtmlId}`;
    const media = htmlDecode(slide.querySelector(selector).innerHTML);
    setHtml(container, media);
};

function updateProductInfo(container, productInfo) {
    const { product_vendor, linkout_url, product_price, product_compare_at_price, caption } =
        productInfo;
    const vendor = container.querySelector(".rebellt-ecommerce--vendor");
    const sum = container.querySelector(".rebellt-ecommerce--sum");
    const price = container.querySelector(".rebellt-ecommerce--price");
    const compareAtPrice = container.querySelector(".rebellt-ecommerce--compare-at-price");
    const priceNumber = container.querySelector(".rebellt-ecommerce--price-number");
    const linkoutBtn = container.querySelector(".rebellt-ecommerce--btn");

    if (product_vendor) {
        vendor.classList.remove("hidden");
        vendor.textContent = product_vendor;
    } else {
        vendor.classList.add("hidden");
    }

    if (linkout_url || product_price) {
        if (product_price) {
            price.classList.remove("hidden");

            if (product_compare_at_price && product_compare_at_price != product_price) {
                compareAtPrice.classList.remove("hidden");
                compareAtPrice.textContent = `$${product_compare_at_price}`;
            } else {
                compareAtPrice.classList.add("hidden");
            }

            priceNumber.textContent = `$${product_price}`;
        } else {
            price.classList.add("hidden");
        }

        sum.classList.remove("hidden");
        linkoutBtn.href = linkout_url;
        linkoutBtn.textContent = caption;
    } else {
        sum.classList.add("hidden");
    }
}

const setSlideTextHtml = (container, slide) => {
    const slideTextHtmlContext = loadTextHtmlContext(slide);

    const headline = container.querySelector(".listicle-slideshow-headline");
    const description = container.querySelector(".listicle-slideshow-description");
    const credit = container.querySelector(".media-photo-credit");
    const caption = container.querySelector(".media-caption");

    if (slideTextHtmlContext) {
        if (slideTextHtmlContext.product_info) {
            updateProductInfo(container, slideTextHtmlContext.product_info);
        }
        if (headline) {
            headline.innerHTML = htmlDecode(slideTextHtmlContext.headline);
        }
        if (description) {
            description.innerHTML = htmlDecode(slideTextHtmlContext.description);
        }
        if (credit) {
            credit.innerHTML = htmlDecode(slideTextHtmlContext.credit);
        }
        if (caption) {
            caption.innerHTML = htmlDecode(slideTextHtmlContext.caption);
        }
    } else {
        return false;
    }
};

const toggleActiveSlide = (container, activeSlide) => {
    container.querySelectorAll(".listicle-slideshow__thumbnail").forEach((slide) => {
        slide.classList.toggle("active", slide === activeSlide);
    });
};

const loadTextHtmlContext = (activeSlide) => {
    const textHtmlContext = loadContextData(`#${activeSlide.dataset.textHtmlId}`);
    if (Object.keys(textHtmlContext).length) {
        return textHtmlContext;
    } else {
        return false;
    }
};

const loadAdContext = (activeSlide) => {
    const adsContext = loadContextData(`#slideshow-ads-context-${activeSlide.dataset.postId}`);
    if (Object.keys(adsContext).length) {
        return adsContext;
    } else {
        return false;
    }
};

const isAdFrame = (counter, activeSlide) => {
    const adsContext = loadAdContext(activeSlide);
    if (adsContext) {
        return counter.current() > 0 && counter.current() % adsContext.adsOrder[0] === 0;
    } else {
        return false;
    }
};

const getAdHtmlCode = (counter, activeSlide) => {
    const adsContext = loadAdContext(activeSlide);
    if (adsContext) {
        const pos = counter.current();
        const code = adsContext.adCodes[pos] || adsContext.adCodes[0];
        return code;
    } else {
        return "";
    }
};

const initNavigationButtons = (slideshow, slidesBar, setSlide, setAdCode) => {
    const nextButton = slideshow.querySelector(".listicle-slideshow--next");
    const prevButton = slideshow.querySelector(".listicle-slideshow--prev");
    const slides = Array.from(slidesBar.querySelectorAll(".listicle-slideshow__thumbnail"));
    let adsPositionCounter = createCounter(slides.length - 1);
    let adsMediaElementCounter = createCounter(slides.length - 1);

    initClickHandler(nextButton, () => {
        const activeSlide = slideshow.querySelector(".active");
        const next = activeSlide.nextElementSibling;
        adsPositionCounter.next();

        if (isAdFrame(adsPositionCounter, activeSlide)) {
            setAdCode(getAdHtmlCode(adsMediaElementCounter, activeSlide));
        } else {
            setSlide(next || slides[0]);
        }
    });

    initClickHandler(prevButton, () => {
        const activeSlide = slideshow.querySelector(".active");
        const prev = activeSlide.previousElementSibling;

        if (isAdFrame(adsPositionCounter, activeSlide)) {
            setAdCode(getAdHtmlCode(adsMediaElementCounter, activeSlide));
        } else {
            setSlide(prev || slides[slides.length - 1]);
        }

        adsPositionCounter.prev();
    });
};

const initThumbClick = (slidesBar, setSlide) => {
    slidesBar.querySelectorAll(".listicle-slideshow__thumbnail").forEach((slide) => {
        slide.addEventListener("click", (event) => {
            setSlide(event.currentTarget);
        });
    });
};

const updateSlidesCounter = (slideshow, activeSlide) => {
    const slideCountWrapper = slideshow.querySelector(".listicle-slideshow__counter");
    if (slideCountWrapper) {
        slideCountWrapper.querySelector(".listicle-slideshow__counter--current").innerText =
            activeSlide.dataset.gotoSlide;
    }
};

const triggerUrlChange = (slide, eventBus) => {
    const { href, postId, pageTitle } = slide.dataset;
    eventBus.publish(URL_CHANGE_EVENT, {
        url: href,
        id: postId,
        headline: pageTitle || "",
        trackPageView: true, // @NOTE: add context and use from engine setting
    });
};

const initSetSlideContext = (context, eventBus) => {
    const { slidesBar, slideshow } = context;
    const container = slideshow.querySelector(".listicle-slideshow-media-html");

    container.style.minHeight = container.getBoundingClientRect().height;

    const allowUrlUpdate = slideshow.dataset.allowUrlUpdate !== "false";

    return {
        setSlide(slide) {
            setSlidesBarPosition(slidesBar, slide);
            setSlideHtml(container, slide);
            setSlideTextHtml(slideshow, slide);
            toggleActiveSlide(slidesBar, slide);
            updateSlidesCounter(slideshow, slide);
            if (allowUrlUpdate) {
                triggerUrlChange(slide, eventBus);
            }
            return slide;
        },
        setAdCode(adCode) {
            if (adCode) {
                setHtml(container, adCode);
            }
            return adCode;
        },
    };
};

const REBEL_LT_ITEM_HASH = "#rebelltitem";

function getFragmentSelectedSlide(slideshow) {
    const { hash } = document.location;

    if (hash.indexOf(REBEL_LT_ITEM_HASH) !== 0) {
        return null;
    }

    const particleIndex = parseInt(hash.substr(REBEL_LT_ITEM_HASH.length), 10);

    if (isNaN(particleIndex)) {
        return null;
    }

    const slide = slideshow.querySelector(
        `.listicle-slideshow__thumbnails [data-goto-slide="${particleIndex}"]`
    );
    return slide;
}

export default function assemblerSlideshow(context, slideshow) {
    const eventBus = context.eventBus;
    const slidesBar = slideshow.querySelector(".listicle-slideshow__thumbnails");

    const selectedFragment = getFragmentSelectedSlide(slideshow);

    const { setSlide, setAdCode } = initSetSlideContext(
        {
            slideshow,
            slidesBar,
        },
        eventBus
    );

    if (selectedFragment) {
        setSlide(selectedFragment);
    }

    initNavigationButtons(slideshow, slidesBar, setSlide, setAdCode);
    initThumbClick(slidesBar, setSlide);
    const activeThumb = slidesBar.querySelector(".listicle-slideshow__thumbnail.active");
    setSlidesBarPosition(slidesBar, activeThumb);
}
